import {useCallback, useEffect, useState} from 'react';

/**
 * This hook allows setting styles on document.body while a component is mounted and the hook is enabled.
 *
 * The original body style is restored upon unmount or when enabled becomes false.
 * Avoid using this hook with the same property and different values simultaneously.
 * @param property the CSS style to set
 * @param value
 * @param enabled
 */
export default function useBodyStyleWhileMounted<T extends keyof CSSStyleDeclaration>(
  property: T,
  value: CSSStyleDeclaration[T],
  enabled: boolean = true
): void {
  const [originalValue, setOriginalValue] = useState<CSSStyleDeclaration[T] | null>(null);

  const cleanup = useCallback(() => {
    if (originalValue != null) {
      document.body.style[property] = originalValue;
      setOriginalValue(null);
    }
  }, [property, originalValue]);

  useEffect(() => {
    if (enabled) {
      if (originalValue == null) {
        setOriginalValue(document.body.style[property]);
        document.body.style[property] = value;
      }
    } else {
      cleanup();
    }

    return cleanup;
  }, [cleanup, property, enabled, value, originalValue]);
}
